import React, {useEffect, useState} from 'react'
import {useParams} from 'react-router-dom'
import {getPublicJobPostById} from '../../../api/HiringAPI'
import PublicApplicationForm from './PublicApplicationForm'

const PublicJD: React.FC = () => {
  const params = useParams()
  const {id} = params
  const [jd, setJd] = useState<any>(null)
  const [error, setError] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  
  // Extract ID from URL pathname as fallback
  const pathId = window.location.pathname.split('/jd/')[1]
  
  // Use the ID from useParams, or fallback to pathname extraction
  const jobId = id || pathId

  useEffect(() => {
    let mounted = true
    ;(async () => {
      if (!jobId) {
        setError('Missing jobPostId')
        setLoading(false)
        return
      }
      
      setLoading(true)
      try {
        const res = await getPublicJobPostById(jobId)
        
        if (!mounted) return
        
        const jobData = res?.data || res
        
        if (jobData?.id) {
          setJd(jobData)
          setError('') // Clear any previous errors
        } else {
          setError('Job not found')
          setJd(null) // Clear job data on error
        }
      } catch (e: any) {
        console.error('Failed to load job details:', e)
        if (!mounted) return
        setError(e?.message || 'Failed to load job details')
      } finally {
        if (mounted) setLoading(false)
      }
    })()
    return () => { mounted = false }
  }, [jobId])
  
  if (loading) return <div className='container py-10'>Loading...</div>
  if (error) return <div className='container py-10'><div className='alert alert-danger'>{error}</div></div>
  if (!jd) return null

  return (
    <div className='container py-10'>
      <div className='mb-10'>
        <h1 className='fw-bold'>{jd.title}</h1>
        <div className='text-muted'>
          {jd.department} {jd.location ? `• ${jd.location}` : ''} • {jd.employmentType}
        </div>
        <div className='mt-6'>
          <h5 className='fw-bold'>Job Description</h5>
          <p>{jd.description}</p>
        </div>
        
        {jd.minExperience || jd.maxExperience ? (
          <div className='mt-6'>
            <h5 className='fw-bold'>Experience Required</h5>
            <p>
              {jd.minExperience && jd.maxExperience 
                ? `${jd.minExperience} - ${jd.maxExperience} years`
                : jd.minExperience 
                ? `${jd.minExperience}+ years`
                : `Up to ${jd.maxExperience} years`}
            </p>
          </div>
        ) : null}
        
        {jd.skillsRequired && jd.skillsRequired.length > 0 ? (
          <div className='mt-6'>
            <h5 className='fw-bold'>Skills Required</h5>
            <div className='d-flex flex-wrap gap-2'>
              {jd.skillsRequired.map((skill: string, idx: number) => (
                <span key={idx} className='badge badge-light-primary'>{skill}</span>
              ))}
            </div>
          </div>
        ) : null}
        
        {jd.compensationMin || jd.compensationMax ? (
          <div className='mt-6'>
            <h5 className='fw-bold'>Compensation</h5>
            <p>
              {jd.currency} {jd.compensationMin && jd.compensationMax 
                ? `${jd.compensationMin} - ${jd.compensationMax}`
                : jd.compensationMin 
                ? `${jd.compensationMin}+`
                : `Up to ${jd.compensationMax}`}
            </p>
          </div>
        ) : null}
        
        <div className='mt-6'>
          <h5 className='fw-bold'>Work Mode</h5>
          <p>{jd.workMode}</p>
        </div>
        
        <div className='mt-6'>
          <h5 className='fw-bold'>Openings</h5>
          <p>{jd.openings} position(s)</p>
        </div>
      </div>
      
      <div className='separator my-10'></div>
      
      <div className='mb-10'>
        <h3 className='fw-bold mb-5'>Apply for this position</h3>
        <PublicApplicationForm jobPostId={jd.id} />
      </div>
    </div>
  )
}

export default PublicJD


